import { reconcileRuntimeSnapshot } from "@/lib/runtime-snapshot-stability"
import {
  applyClaudeBaselineSyncEventsBatch,
} from "@/lib/claude-raw-runtime"
import {
  computeRawSyncEventsBatch,
} from "@/lib/agent-runtime-sync-batch-computation"
import {
  type ManagedRuntimeState,
} from "@/lib/thread-sync-replica-runtime"
import type {
  ThreadSyncHttpCatchupResponse,
  ThreadSyncHttpPrefillBaselineEvent,
  ThreadSyncHttpPrefillCatchupEvent,
} from "@/lib/thread-sync-http-catchup-client"
import type {
} from "@/lib/thread-sync-background-warm-protocol"
function normalizeTimestamp(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return null
  }

  return Math.floor(value)
}

export function applyThreadSyncBaselineEventsToManagedRuntimeState(args: {
  currentState: ManagedRuntimeState
  events: ThreadSyncHttpPrefillBaselineEvent[]
}): ManagedRuntimeState {
  if (args.events.length === 0) {
    return args.currentState
  }

  const applied = applyClaudeBaselineSyncEventsBatch({
    projection: args.currentState.rawClaudeProjection,
    events: args.events.map((event) => ({
      baselineSeq: event.baselineSeq,
      eventType: event.type,
      payload: event.payload,
      timestamp: event.timestamp,
    })),
  })
  if (!applied) {
    return args.currentState
  }

  const previousSnapshot =
    args.currentState.agentType === "claude_code"
      ? args.currentState.snapshot
      : null
  const stableProviderEventId =
    typeof previousSnapshot?.cursorState.providerEventId === "number" &&
    Number.isFinite(previousSnapshot.cursorState.providerEventId) &&
    previousSnapshot.cursorState.providerEventId > 0
      ? Math.floor(previousSnapshot.cursorState.providerEventId)
      : args.currentState.lastEventId
  const baselineSnapshot = {
    ...applied.snapshot,
    cursorState: {
      ...applied.snapshot.cursorState,
      providerEventId: stableProviderEventId ?? null,
    },
  }

  return {
    agentType: "claude_code",
    snapshot: reconcileRuntimeSnapshot(previousSnapshot, baselineSnapshot),
    rawOpenCodeProjection: null,
    rawClaudeProjection: applied.projection,
    rawCursorProjection: null,
    lastAppliedTimestamp:
      typeof args.currentState.lastAppliedTimestamp === "number" &&
      args.currentState.lastAppliedTimestamp > 0
        ? Math.max(args.currentState.lastAppliedTimestamp, applied.lastAppliedTimestamp)
        : applied.lastAppliedTimestamp,
    lastEventId: args.currentState.lastEventId,
  }
}

export function applyThreadSyncCatchupEventsToManagedRuntimeState(args: {
  currentState: ManagedRuntimeState
  events: ThreadSyncHttpPrefillCatchupEvent[]
}): ManagedRuntimeState {
  if (args.events.length === 0) {
    return args.currentState
  }

  return computeRawSyncEventsBatch({
    currentState: args.currentState,
    events: args.events.map((event) => ({
      eventType: event.type,
      payload: event.payload,
      eventId: event.id,
      childSessionId: event.childSessionId ?? null,
      timestamp: event.timestamp,
    })),
  }).nextState
}

export function advanceManagedRuntimeStateCursor(args: {
  currentState: ManagedRuntimeState
  nextCursor: number
}): ManagedRuntimeState {
  const normalizedNextCursor = normalizeTimestamp(args.nextCursor)
  if (normalizedNextCursor === null) {
    return args.currentState
  }

  const currentLastEventId = normalizeTimestamp(args.currentState.lastEventId)
  const nextLastEventId = Math.max(currentLastEventId ?? 0, normalizedNextCursor)
  const currentProviderEventId = normalizeTimestamp(
    args.currentState.snapshot?.cursorState.providerEventId,
  )
  const nextProviderEventId = Math.max(currentProviderEventId ?? 0, normalizedNextCursor)
  const nextSnapshot = args.currentState.snapshot === null
    ? null
    : currentProviderEventId === nextProviderEventId
      ? args.currentState.snapshot
      : {
          ...args.currentState.snapshot,
          cursorState: {
            ...args.currentState.snapshot.cursorState,
            providerEventId: nextProviderEventId,
          },
        }

  if (
    nextSnapshot === args.currentState.snapshot &&
    nextLastEventId === currentLastEventId
  ) {
    return args.currentState
  }

  return {
    ...args.currentState,
    snapshot: nextSnapshot,
    lastEventId: nextLastEventId,
  }
}

export function shouldInvalidateThreadSyncBackgroundWarmPrefix(args: {
  requestedAfter: number
  previousHistoryToken: string | null
  response: Pick<
    ThreadSyncHttpCatchupResponse,
    "acceptedCursor" | "historyContinuity" | "historyToken"
  >
}): boolean {
  if (args.requestedAfter <= 0) {
    return false
  }

  if (args.response.historyContinuity !== "contiguous") {
    return true
  }

  if (args.response.acceptedCursor !== args.requestedAfter) {
    return true
  }

  if (
    typeof args.previousHistoryToken === "string" &&
    args.previousHistoryToken.trim().length > 0 &&
    args.response.historyToken !== args.previousHistoryToken
  ) {
    return true
  }

  return false
}
